import React, { useState } from 'react';
import {
  FormInput, Plus, Trash2, GripVertical, ChevronDown, ChevronUp,
  Type, Hash, Calendar, List, ToggleLeft, AlignLeft, Settings2,
  AlertCircle, CheckCircle2
} from 'lucide-react';

const FIELD_TYPES = [
  { value: 'text',         label: 'Text',         icon: Type },
  { value: 'number',       label: 'Number',        icon: Hash },
  { value: 'date',         label: 'Date',          icon: Calendar },
  { value: 'dropdown',     label: 'Dropdown',      icon: List },
  { value: 'multi_select', label: 'Multi Select',  icon: List },
  { value: 'boolean',      label: 'Toggle',        icon: ToggleLeft },
];

const MODES = [
  { value: 'pre_run_modal',  label: 'Pre-Run Modal',   desc: 'Show form before flow starts' },
  { value: 'inline_panel',   label: 'Inline Panel',    desc: 'Fill inside the editor before Run' },
  { value: 'mid_flow_pause', label: 'Mid-Flow Pause',  desc: 'Pause at this node and wait for input' },
];

function FieldTypeIcon({ type, size = 14 }) {
  const entry = FIELD_TYPES.find(f => f.value === type);
  if (!entry) return <FormInput size={size} />;
  const Icon = entry.icon;
  return <Icon size={size} />;
}

function OptionBuilder({ options = [], onChange }) {
  const addOption = () => onChange([...options, { label: '', value: '' }]);
  const remove = (i) => onChange(options.filter((_, idx) => idx !== i));
  const update = (i, key, val) => {
    const next = [...options];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };

  return (
    <div className="mt-2 space-y-1">
      <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider mb-1">Options</p>
      {options.map((opt, i) => (
        <div key={i} className="flex gap-1 items-center">
          <input
            className="flex-1 text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100
                       focus:outline-none focus:ring-1 focus:ring-amber-400"
            placeholder="Label"
            value={opt.label}
            onChange={e => update(i, 'label', e.target.value)}
          />
          <input
            className="flex-1 text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100
                       focus:outline-none focus:ring-1 focus:ring-amber-400"
            placeholder="Value"
            value={opt.value}
            onChange={e => update(i, 'value', e.target.value)}
          />
          <button onClick={() => remove(i)} className="text-red-400 hover:text-red-600 p-0.5">
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <button
        onClick={addOption}
        className="text-[11px] text-amber-600 hover:text-amber-700 flex items-center gap-1 mt-1"
      >
        <Plus size={11} /> Add option
      </button>
    </div>
  );
}

function FieldCard({ field, index, onUpdate, onRemove }) {
  const [expanded, setExpanded] = useState(false);

  const update = (key, value) => onUpdate(index, { ...field, [key]: value });
  const nameFromLabel = (label) => label.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/__+/g, '_');

  const hasOptions = ['dropdown', 'multi_select'].includes(field.type);

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/60">
        <GripVertical size={14} className="text-slate-400 cursor-grab flex-shrink-0" />
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <FieldTypeIcon type={field.type} size={13} />
          <span className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">
            {field.label || <span className="italic text-slate-400">Untitled field</span>}
          </span>
          {field.required && (
            <span className="text-[9px] bg-red-100 dark:bg-red-900/30 text-red-600
                             dark:text-red-400 px-1 rounded font-medium">REQ</span>
          )}
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        <button onClick={() => onRemove(index)} className="text-red-400 hover:text-red-600">
          <Trash2 size={13} />
        </button>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 py-2.5 space-y-2.5 bg-white dark:bg-slate-900/40">
          {/* Label */}
          <div>
            <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Label</label>
            <input
              className="w-full mt-0.5 text-xs px-2 py-1.5 rounded border border-slate-200
                         dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800
                         dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-400"
              value={field.label}
              onChange={e => {
                const label = e.target.value;
                update('label', label);
                // Auto-generate name if it hasn't been customised
                if (!field._nameCustomised) {
                  update('name', nameFromLabel(label));
                }
              }}
              placeholder="e.g. Report Date"
            />
          </div>

          {/* Field Name (key) */}
          <div>
            <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">
              Field Name <span className="text-slate-400 normal-case font-normal">(used as input.name)</span>
            </label>
            <input
              className="w-full mt-0.5 text-xs px-2 py-1.5 rounded border border-slate-200
                         dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800
                         dark:text-slate-100 font-mono focus:outline-none focus:ring-1 focus:ring-amber-400"
              value={field.name}
              onChange={e => { update('name', e.target.value); update('_nameCustomised', true); }}
              placeholder="report_date"
            />
          </div>

          {/* Type */}
          <div>
            <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Type</label>
            <select
              className="w-full mt-0.5 text-xs px-2 py-1.5 rounded border border-slate-200
                         dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800
                         dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-400"
              value={field.type}
              onChange={e => update('type', e.target.value)}
            >
              {FIELD_TYPES.map(ft => (
                <option key={ft.value} value={ft.value}>{ft.label}</option>
              ))}
            </select>
          </div>

          {/* Required + Default */}
          <div className="flex gap-2">
            <label className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={field.required ?? true}
                onChange={e => update('required', e.target.checked)}
                className="w-3.5 h-3.5 accent-amber-500"
              />
              Required
            </label>
          </div>

          {/* Default Value */}
          {field.type !== 'boolean' && (
            <div>
              <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Default Value</label>
              <input
                className="w-full mt-0.5 text-xs px-2 py-1.5 rounded border border-slate-200
                           dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800
                           dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-400"
                value={field.defaultValue ?? ''}
                onChange={e => update('defaultValue', e.target.value || null)}
                placeholder="Leave empty for no default"
              />
            </div>
          )}

          {/* Placeholder */}
          {['text', 'number'].includes(field.type) && (
            <div>
              <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Placeholder</label>
              <input
                className="w-full mt-0.5 text-xs px-2 py-1.5 rounded border border-slate-200
                           dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800
                           dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-400"
                value={field.placeholder ?? ''}
                onChange={e => update('placeholder', e.target.value || null)}
                placeholder="e.g. Enter value…"
              />
            </div>
          )}

          {/* Help Text */}
          <div>
            <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Help Text</label>
            <input
              className="w-full mt-0.5 text-xs px-2 py-1.5 rounded border border-slate-200
                         dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800
                         dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-400"
              value={field.helpText ?? ''}
              onChange={e => update('helpText', e.target.value || null)}
              placeholder="Shown below the field as a hint"
            />
          </div>

          {/* Options for dropdown / multi_select */}
          {hasOptions && (
            <OptionBuilder
              options={field.options || []}
              onChange={opts => update('options', opts)}
            />
          )}

          {/* Placeholder hint */}
          {field.name && (
            <div className="text-[10px] text-slate-400 bg-slate-50 dark:bg-slate-800/60
                            rounded px-2 py-1 font-mono">
              Use <span className="text-amber-600 dark:text-amber-400">input.{field.name}</span> in
              downstream nodes
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function UserInputNodePanel({ data = {}, onChange }) {
  const fields = data.fields || [];
  const mode = data.mode || 'pre_run_modal';
  const formTitle = data.form_title || 'Flow Parameters';
  const formDescription = data.form_description || '';

  const [showSettings, setShowSettings] = useState(false);

  const update = (patch) => onChange?.({ ...data, ...patch });

  const addField = () => {
    const newField = {
      name: `field_${fields.length + 1}`,
      label: `Field ${fields.length + 1}`,
      type: 'text',
      required: true,
      defaultValue: null,
      placeholder: null,
      helpText: null,
      options: [],
      _nameCustomised: false,
    };
    update({ fields: [...fields, newField] });
  };

  const updateField = (index, updatedField) => {
    const next = [...fields];
    next[index] = updatedField;
    update({ fields: next });
  };

  const removeField = (index) => {
    update({ fields: fields.filter((_, i) => i !== index) });
  };

  const selectedMode = MODES.find(m => m.value === mode);
  const hasBlockingSchedule = fields.some(f => f.required && !f.defaultValue) &&
    mode !== 'inline_panel';

  return (
    <div className="flex flex-col gap-3 p-3 text-sm">
      {/* Node header */}
      <div className="flex items-center gap-2">
        <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded">
          <FormInput size={16} className="text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">User Input</p>
          <p className="text-[10px] text-slate-500">Collect runtime values from the operator</p>
        </div>
      </div>

      {/* Mode selector */}
      <div>
        <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">
          Presentation Mode
        </label>
        <div className="mt-1 space-y-1">
          {MODES.map(m => (
            <label
              key={m.value}
              className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                mode === m.value
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-500'
                  : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              }`}
            >
              <input
                type="radio"
                name="mode"
                value={m.value}
                checked={mode === m.value}
                onChange={() => update({ mode: m.value })}
                className="mt-0.5 accent-amber-500"
              />
              <div>
                <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{m.label}</p>
                <p className="text-[10px] text-slate-400">{m.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Schedule warning */}
      {mode === 'mid_flow_pause' && (
        <div className="flex gap-2 p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20
                        border border-orange-200 dark:border-orange-700">
          <AlertCircle size={13} className="text-orange-500 mt-0.5 flex-shrink-0" />
          <p className="text-[10px] text-orange-700 dark:text-orange-400">
            Mid-flow pause is <strong>incompatible with scheduled runs</strong>. Use defaults
            on all fields if you need scheduling.
          </p>
        </div>
      )}

      {/* Form Settings (collapsible) */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium
                     text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40"
        >
          <span className="flex items-center gap-1.5">
            <Settings2 size={12} />
            Form Settings
          </span>
          {showSettings ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
        {showSettings && (
          <div className="px-3 pb-3 pt-1 space-y-2 bg-white dark:bg-slate-900/40">
            <div>
              <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">
                Form Title
              </label>
              <input
                className="w-full mt-0.5 text-xs px-2 py-1.5 rounded border border-slate-200
                           dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800
                           dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-400"
                value={formTitle}
                onChange={e => update({ form_title: e.target.value })}
                placeholder="Flow Parameters"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">
                Form Description
              </label>
              <textarea
                rows={2}
                className="w-full mt-0.5 text-xs px-2 py-1.5 rounded border border-slate-200
                           dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800
                           dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-400
                           resize-none"
                value={formDescription}
                onChange={e => update({ form_description: e.target.value })}
                placeholder="Enter values below to configure the flow run…"
              />
            </div>
          </div>
        )}
      </div>

      {/* Field count */}
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">
          Input Fields ({fields.length})
        </p>
        {hasBlockingSchedule && (
          <span className="text-[9px] text-orange-500 flex items-center gap-0.5">
            <AlertCircle size={9} /> Required fields block scheduling
          </span>
        )}
      </div>

      {/* Fields list */}
      <div className="space-y-2">
        {fields.length === 0 && (
          <div className="text-center py-6 text-slate-400 border border-dashed border-slate-200
                          dark:border-slate-700 rounded-lg">
            <FormInput size={24} className="mx-auto mb-2 opacity-40" />
            <p className="text-xs">No fields yet. Add one below.</p>
          </div>
        )}
        {fields.map((field, i) => (
          <FieldCard
            key={i}
            index={i}
            field={field}
            onUpdate={updateField}
            onRemove={removeField}
          />
        ))}
      </div>

      {/* Add Field button */}
      <button
        onClick={addField}
        className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg
                   border border-dashed border-amber-300 dark:border-amber-700
                   text-amber-600 dark:text-amber-400 text-xs font-medium
                   hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
      >
        <Plus size={13} /> Add Input Field
      </button>

      {/* Downstream usage hint */}
      {fields.length > 0 && (
        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2">
          <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider mb-1">
            Placeholder Usage
          </p>
          {fields.map(f => f.name && (
            <p key={f.name} className="text-[10px] font-mono text-slate-600 dark:text-slate-400">
              <span className="text-amber-600 dark:text-amber-400">input.{f.name}</span>
              <span className="text-slate-400"> — {f.label}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
